/**
 * ResetPositionComponent - respawns actors that fall below a world threshold.
 */

import * as THREE from 'three';

import { PhysicsVectorParam } from '../physics/PhysicsEngine.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { property } from '../utils/serialization/decorator.js';

import { PrimitiveComponent } from './PrimitiveComponent.js';
import { NON_SPATIAL, SceneComponent, type SceneComponentOptions } from './SceneComponent.js';

import type { PhysicsTransformUpdateFlags } from './PrimitiveComponent.js';

export interface ResetPositionOptions extends SceneComponentOptions {
  /** The position to reset to when the attached object falls below the threshold */
  resetPosition?: THREE.Vector3;
  /** Y-coordinate below which the object is considered fallen off. Default: -50 */
  fallThreshold?: number;
  /** Whether to reset velocities when teleporting. Default: true */
  resetVelocities?: boolean;
}

/**
 * Component that resets an object's position when it falls below a certain threshold
 */
@EngineClass('ResetPositionComponent')
export class ResetPositionComponent extends SceneComponent {
  static readonly [NON_SPATIAL] = true;

  @property({ description: 'The position to reset to when fallen' })
  protected resetPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property({ type: 'number', step: 1, decimals: 0, description: 'Y-coordinate threshold for reset' })
  protected fallThreshold: number = -50;

  @property({ description: 'Whether to reset velocities when teleporting' })
  protected resetVelocities: boolean = true;

  public override initialize(options?: ResetPositionOptions): void {
    super.initialize(options);
    if (options?.resetPosition !== undefined) this.resetPosition = options.resetPosition.clone();
    this.fallThreshold = options?.fallThreshold ?? this.fallThreshold;
    this.resetVelocities = options?.resetVelocities ?? this.resetVelocities;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.checkPosition();
  }

  private checkPosition(): void {
    const actor = this.getActor();
    if (!actor) return;

    const rootComponent = actor.rootComponent;
    const position = new THREE.Vector3();
    rootComponent.getWorldPosition(position);

    if (position.y < this.fallThreshold) {
      console.log(`Object fell off the map! Requesting teleport to ${this.resetPosition.x}, ${this.resetPosition.y}, ${this.resetPosition.z}`);
      this.applyTeleport();
    }
  }

  private applyTeleport(): void {
    const actor = this.getActor();
    if (!actor) return;

    const teleportPos = this.resetPosition.clone();
    teleportPos.y += 1.0; // Add height offset to prevent ground clipping

    const rootComponent = actor.rootComponent;
    const physicsEngine = this.getPhysicsEngine();

    console.log(`Teleporting to ${this.resetPosition.x}, ${this.resetPosition.y}, ${this.resetPosition.z}`);

    if (!physicsEngine || !(rootComponent instanceof PrimitiveComponent)) {
      // No physics involved, just set world position
      rootComponent.setWorldPosition(teleportPos);
    } else {
      // 1. Save the original flags so we can restore them
      const originalFlags = rootComponent.getPhysicsTransformUpdateFlags();

      // 2. Force flags to ensure our transform gets sent to physics
      const forceSendFlags: PhysicsTransformUpdateFlags = {
        ...originalFlags,
        sendPosition: true,
        sendRotation: true,
        // Prevent physics from immediately overriding our position
        receivePosition: false,
        receiveRotation: false
      };
      rootComponent.setPhysicsTransformUpdateFlags(forceSendFlags);

      // 3. Reset velocities if enabled
      if (this.resetVelocities) {
        physicsEngine.setVectorParam(
          rootComponent,
          PhysicsVectorParam.LinearVelocity,
          [0, 0, 0]
        );

        physicsEngine.setVectorParam(
          rootComponent,
          PhysicsVectorParam.AngularVelocity,
          [0, 0, 0]
        );
      }

      // 4. Set teleport position with slight Y offset to prevent clipping
      rootComponent.setWorldPosition(teleportPos);

      // 5. Force update the physics transform
      physicsEngine.updateTransform(rootComponent);

      // 6. Restore original flags after a small delay
      setTimeout(() => {
        rootComponent.setPhysicsTransformUpdateFlags(originalFlags);
      }, 100);
    }
  }

  public setResetPosition(position: THREE.Vector3): void {
    this.resetPosition.copy(position);
  }

  public getResetPosition(): THREE.Vector3 {
    return this.resetPosition;
  }

  public setFallThreshold(threshold: number): void {
    this.fallThreshold = threshold;
  }

  public getFallThreshold(): number {
    return this.fallThreshold;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_ResetPosition';
  }
}
